import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Minus, Plus, ChevronRight, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCart } from '@/contexts/CartContext';
import ProductCard from '@/components/ProductCard';
import { TrustSignals, ReturnPolicy } from '@/components/TrustBadges';
import api, { formatPrice } from '@/lib/api';

export default function ProductPage() {
    const { slug } = useParams();
    const { addToCart } = useCart();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedVariant, setSelectedVariant] = useState(null); // rich variant_options entry
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/products/${slug}`);
                if (cancelled) return;
                setProduct(data);
                setSelectedImage(0);
                setQuantity(1);
                const opts = data?.variant_options || [];
                const firstAvailable = opts.find(o => (o.stock_quantity ?? 1) > 0) || opts[0] || null;
                setSelectedVariant(firstAvailable);
                if (data.category_id) {
                    try {
                        const { data: r } = await api.get(`/products?category=${data.category_id}&limit=4`);
                        if (!cancelled) setRelated((r.products || []).filter(p => p.slug !== slug));
                    } catch { /* swallow */ }
                }
            } catch { /* swallow */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-4 border-pink-600 border-t-transparent rounded-full animate-spin" /></div>;
    if (!product) return <div className="text-center py-20 font-body text-gray-500">Product not found</div>;

    const variantOptions = product.variant_options || [];
    const hasVariants = variantOptions.length > 0;
    const basePrice = product.sale_price || product.price || 0;
    // Variant pricing: explicit `price` (override) wins; fall back to base + legacy `price_adjustment`
    const variantPrice = (() => {
        if (!selectedVariant) return null;
        if (selectedVariant.price != null && selectedVariant.price !== '') return Number(selectedVariant.price);
        const adj = Number(selectedVariant.price_adjustment || 0);
        return basePrice + adj;
    })();
    const effectivePrice = hasVariants && variantPrice != null ? Math.max(0, variantPrice) : basePrice;
    const effectiveStock = hasVariants
        ? Math.max(0, Number(selectedVariant?.stock_quantity ?? 0))
        : Number(product.stock || 0);
    const hasDiscount = product.sale_price && product.sale_price < product.price;
    const baseImages = product.images?.length ? product.images : ['https://via.placeholder.com/400'];
    // When a variant with its own image is selected, surface it as the primary view at index 0
    const images = (selectedVariant?.image && !baseImages.includes(selectedVariant.image))
        ? [selectedVariant.image, ...baseImages]
        : baseImages;
    const productAlt = `${product.name}${selectedVariant?.variant_name ? ' — ' + selectedVariant.variant_name : ''} - ${product.category_name || 'BeautyByChancy'} premium skincare product`;

    return (
        <div className="min-h-screen bg-white" data-testid="product-page">
            {/* SEO: hidden h1 for crawlers, visible breadcrumb for users */}
            <title>{product.name} | BeautyByChancy - Premium Skincare Abuja</title>
            <div className="max-w-7xl mx-auto px-4 py-4">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm font-body text-gray-500 mb-6 flex-wrap">
                    <Link to="/" className="hover:text-pink-600">Home</Link><ChevronRight className="w-3 h-3" />
                    <Link to="/shop" className="hover:text-pink-600">Shop</Link><ChevronRight className="w-3 h-3" />
                    {product.category_name && <><Link to={`/shop/${product.category_slug || product.category_id}`} className="hover:text-pink-600" data-testid="breadcrumb-category">{product.category_name}</Link><ChevronRight className="w-3 h-3" /></>}
                    <span className="text-gray-900 truncate">{product.name}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                    {/* Images */}
                    <div className="space-y-3">
                        <div className="aspect-square bg-gray-50 rounded-2xl overflow-hidden">
                            <img src={images[selectedImage]} alt={productAlt} loading="eager" fetchPriority="high" decoding="async" width={800} height={800} sizes="(max-width: 1024px) 100vw, 50vw" className="w-full h-full object-cover" data-testid="product-main-image" />
                        </div>
                        {images.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto">
                                {images.map((img, i) => (
                                    <button key={i} onClick={() => setSelectedImage(i)}
                                        className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 ${i === selectedImage ? 'border-pink-600' : 'border-gray-200'}`}>
                                        <img src={img} alt={`${product.name} view ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="space-y-5">
                        <div>
                            {product.category_name && (
                                <Link
                                    to={`/shop/${product.category_slug || product.category_id}`}
                                    className="inline-flex items-center text-xs font-body text-pink-600 hover:text-pink-700 uppercase tracking-[0.2em] font-bold mb-2 transition-colors hover:underline underline-offset-4"
                                    data-testid="product-category-link"
                                >
                                    {product.category_name}
                                </Link>
                            )}
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-testid="product-name">{product.name}</h1>
                        </div>
                        <div className="flex items-baseline gap-3">
                            <span className="text-3xl font-bold text-pink-600 font-body" data-testid="product-price">{formatPrice(effectivePrice)}</span>
                            {hasDiscount && !selectedVariant && <span className="text-lg text-gray-400 line-through font-body">{formatPrice(product.price)}</span>}
                            {hasDiscount && !selectedVariant && <Badge className="bg-pink-100 text-pink-600 rounded-full">{Math.round((1 - product.sale_price / product.price) * 100)}% OFF</Badge>}
                        </div>
                        <p className="font-body text-gray-600 leading-relaxed" data-testid="product-description">{product.description}</p>

                        {/* Rich Variant Swatches — only when variant_options exist */}
                        {hasVariants && (
                            <div className="space-y-2" data-testid="variant-swatches">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-body font-semibold text-gray-700">Choose option</span>
                                    {selectedVariant?.variant_name && (
                                        <span className="text-xs font-body text-pink-600" data-testid="variant-selected-name">{selectedVariant.variant_name}</span>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                    {variantOptions.map((opt, i) => {
                                        const active = selectedVariant?.variant_name === opt.variant_name;
                                        const out = (opt.stock_quantity ?? 0) <= 0;
                                        const optPrice = (opt.price != null && opt.price !== '')
                                            ? Math.max(0, Number(opt.price))
                                            : Math.max(0, basePrice + Number(opt.price_adjustment || 0));
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => { if (!out) { setSelectedVariant(opt); setSelectedImage(0); } }}
                                                disabled={out}
                                                className={`relative rounded-xl border-2 p-2 text-left transition-all active:scale-[0.97] tap-target ${
                                                    active
                                                        ? 'border-pink-500 bg-pink-50/70 shadow-sm'
                                                        : out
                                                            ? 'border-gray-100 bg-gray-50/60 opacity-50 cursor-not-allowed'
                                                            : 'border-gray-200 bg-white hover:border-pink-200 hover:bg-pink-50/30'
                                                }`}
                                                data-testid={`variant-swatch-${i}`}
                                                title={out ? `${opt.variant_name} — out of stock` : opt.variant_name}
                                            >
                                                {opt.image && (
                                                    <div className="w-full aspect-square rounded-md overflow-hidden mb-1.5 bg-gray-50">
                                                        <img src={opt.image} alt={opt.variant_name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                                    </div>
                                                )}
                                                <div className={`text-xs font-body font-semibold leading-tight ${active ? 'text-pink-700' : 'text-gray-900'}`}>
                                                    {opt.variant_name}
                                                </div>
                                                <div className="text-[11px] font-body text-gray-500 mt-0.5">{formatPrice(optPrice)}</div>
                                                {active && (
                                                    <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-pink-500 text-white text-[9px] font-bold flex items-center justify-center">✓</span>
                                                )}
                                                {out && (
                                                    <span className="absolute top-1.5 left-1.5 bg-gray-700 text-white text-[8px] font-body uppercase tracking-wider px-1 rounded">Out</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {product.skin_type && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-body text-gray-500">Skin Type:</span>
                                <Badge variant="outline" className="rounded-full font-body">{product.skin_type}</Badge>
                            </div>
                        )}

                        <div className="flex items-center gap-2 text-sm font-body">
                            <div className={`w-2 h-2 rounded-full ${effectiveStock > 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className={effectiveStock > 0 ? 'text-green-600' : 'text-red-500'} data-testid="product-stock-label">
                                {effectiveStock > 0 ? `In Stock (${effectiveStock} available)` : 'Out of Stock'}
                                {hasVariants && selectedVariant?.variant_name ? ` — ${selectedVariant.variant_name}` : ''}
                            </span>
                        </div>

                        {/* Quantity + Add to Cart */}
                        <div className="flex items-center gap-4 pt-2">
                            <div className="flex items-center border border-gray-200 rounded-full">
                                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 rounded-l-full" data-testid="quantity-decrease"><Minus className="w-4 h-4" /></button>
                                <span className="w-10 text-center font-body font-medium" data-testid="quantity-value">{quantity}</span>
                                <button onClick={() => setQuantity(q => Math.min(Math.max(effectiveStock, 1), q + 1))} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 rounded-r-full" data-testid="quantity-increase"><Plus className="w-4 h-4" /></button>
                            </div>
                            <Button
                                onClick={() => {
                                    const variantStr = hasVariants && selectedVariant ? selectedVariant.variant_name : '';
                                    addToCart(product.id, quantity, variantStr);
                                }}
                                disabled={effectiveStock <= 0}
                                className="flex-1 bg-pink-600 hover:bg-pink-700 rounded-full h-12 text-base font-body gap-2"
                                data-testid="add-to-cart-button"
                            >
                                <ShoppingCart className="w-5 h-5" /> Add to Cart
                            </Button>
                        </div>

                        {/* Trust Signals */}
                        <TrustSignals compact />

                        {/* Return Policy */}
                        <ReturnPolicy />
                    </div>
                </div>

                {/* Tabs */}
                <Tabs defaultValue="description" className="mt-12">
                    <TabsList className="bg-gray-100 rounded-full p-1">
                        <TabsTrigger value="description" className="rounded-full font-body data-[state=active]:bg-white">Description</TabsTrigger>
                        {product.ingredients && <TabsTrigger value="ingredients" className="rounded-full font-body data-[state=active]:bg-white">Ingredients</TabsTrigger>}
                        {product.how_to_use && <TabsTrigger value="how-to-use" className="rounded-full font-body data-[state=active]:bg-white">How to Use</TabsTrigger>}
                    </TabsList>
                    <TabsContent value="description" className="mt-4 font-body text-gray-600 leading-relaxed">{product.description}</TabsContent>
                    <TabsContent value="ingredients" className="mt-4 font-body text-gray-600 leading-relaxed">{product.ingredients}</TabsContent>
                    <TabsContent value="how-to-use" className="mt-4 font-body text-gray-600 leading-relaxed">{product.how_to_use}</TabsContent>
                </Tabs>

                {/* Related */}
                {related.length > 0 && (
                    <section className="mt-14 pb-8">
                        <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">You May Also Like</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                            {related.map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
